import { apiClient } from '../shared/api/apiClient';

export interface ReportDefinition {
  key: string;
  title: string;
  category: string;
  module: string;
  description: string;
  parameters?: any[];
  format_supported?: string[];
}

export interface ReportDataResult {
  metadata: {
    key: string;
    title: string;
    category: string;
    generated_at: string;
    filters_applied: Record<string, any>;
  };
  columns: Array<{ field: string; header: string }>;
  rows: Array<Record<string, any>>;
  execution_time_ms: number;
  total_count: number;
}

export interface ExecutiveKPIs {
  total_students: number;
  active_students: number;
  departments_count: number;
  programs_count: number;
  faculty_count: number;
  fees_collected: number;
  fees_outstanding: number;
  inventory_valuation: number;
  inventory_items_count: number;
  procurement_spend: number;
  vehicles_count: number;
  routes_count: number;
  hostel_occupancy_pct: number;
  library_issued_count: number;
  total_offers: number;
  placement_rate_pct: number;
  ai_sessions_count: number;
  alumni_count: number;
  certificates_count: number;
}

export interface KPISnapshot {
  id: string;
  tenant_schema: string;
  kpi_key: string;
  period: string;
  snapshot_timestamp: string;
  kpi_value: ExecutiveKPIs | Record<string, any>;
  calculation_version: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface SuperAdminTelemetryResponse {
  tenants: {
    total: number;
    active: number;
    suspended: number;
    archived: number;
    provisioning: number;
    overview: Array<{ schema_name: string; name: string; status: string; created_at: string }>;
  };
  kpi_cache: {
    hits: number;
    misses: number;
    fallbacks: number;
    total_requests: number;
    hit_ratio_percent: number;
    configured_backend: string;
  };
  snapshots: {
    total_snapshots: number;
    recent: Array<{ id: string; tenant_schema: string; kpi_key: string; period: string; snapshot_timestamp: string }>;
  };
  export_pipeline: {
    total_jobs: number;
    status_distribution: Record<string, number>;
  };
  infrastructure: {
    database_vendor: string;
    database_connected: boolean;
    redis_cache_status: string;
    celery_eager_mode: boolean;
  };
}

export interface ExportJob {
  id: string;
  report_key: string;
  report_title: string;
  export_format: string;
  parameters: Record<string, any>;
  status: 'PENDING' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  idempotency_key?: string;
  row_count: number;
  file_size_bytes: number;
  output_filename: string;
  content_type: string;
  download_url_ready: boolean;
  error_message?: string;
  retry_count: number;
  user_email?: string;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ExportJobDownloadResponse {
  download_url: string;
  filename: string;
  content_type: string;
  file_size_bytes: number;
  expires_in: number;
}

export const reportingService = {
  getCatalog: async (): Promise<ReportDefinition[]> => {
    const res = await apiClient.get<ReportDefinition[]>('/reporting/reports/');
    return Array.isArray(res.data) ? res.data : [];
  },

  getExecutiveKPIs: async (filters?: any): Promise<ExecutiveKPIs> => {
    const res = await apiClient.get<ExecutiveKPIs>('/reporting/reports/kpis/', { params: filters });
    return res.data;
  },

  getReportData: async (reportKey: string, filters?: any): Promise<ReportDataResult> => {
    const res = await apiClient.post<ReportDataResult>('/reporting/reports/query/', {
      report_key: reportKey,
      filters: filters || {},
    });
    return res.data;
  },

  exportReportUrl: (reportKey: string, format: string = 'csv', filters?: any): string => {
    const queryParams = new URLSearchParams({
      report_key: reportKey,
      export_format: format,
      ...(filters || {}),
    });
    return `/api/reporting/reports/export/?${queryParams.toString()}`;
  },

  createExportJob: async (
    reportKey: string,
    format: string = 'csv',
    filters?: any,
    idempotencyKey?: string,
  ): Promise<ExportJob> => {
    const res = await apiClient.post<ExportJob>('/reporting/exports/', {
      report_key: reportKey,
      export_format: format,
      parameters: filters || {},
      idempotency_key: idempotencyKey,
    });
    return res.data;
  },

  getExportJobStatus: async (jobId: string): Promise<ExportJob> => {
    const res = await apiClient.get<ExportJob>(`/reporting/exports/${jobId}/`);
    return res.data;
  },

  getExportJobDownloadUrl: async (jobId: string): Promise<ExportJobDownloadResponse> => {
    const res = await apiClient.get<ExportJobDownloadResponse>(`/reporting/exports/${jobId}/download-url/`);
    return res.data;
  },

  getExportJobsList: async (): Promise<ExportJob[]> => {
    const res = await apiClient.get<ExportJob[]>('/reporting/exports/');
    return Array.isArray(res.data) ? res.data : [];
  },

  getAuditHistory: async (): Promise<any[]> => {
    const res = await apiClient.get<any[]>('/reporting/reports/history/');
    return Array.isArray(res.data) ? res.data : [];
  },

  getKPISnapshots: async (params?: any): Promise<KPISnapshot[]> => {
    const res = await apiClient.get<KPISnapshot[]>('/reporting/reports/kpis/snapshots/', { params });
    return Array.isArray(res.data) ? res.data : [];
  },

  triggerKPISnapshot: async (period?: string, asyncExecution: boolean = false): Promise<any> => {
    const res = await apiClient.post('/reporting/reports/kpis/snapshots/', {
      period,
      async_execution: asyncExecution,
    });
    return res.data;
  },

  compareKPISnapshots: async (baseId: string, targetId: string): Promise<any> => {
    const res = await apiClient.get('/reporting/reports/kpis/snapshots/compare/', {
      params: { base_id: baseId, target_id: targetId },
    });
    return res.data;
  },

  getSuperAdminTelemetry: async (): Promise<SuperAdminTelemetryResponse> => {
    const res = await apiClient.get<SuperAdminTelemetryResponse>('/tenancy/super-admin/telemetry/');
    return res.data;
  },
};

export default reportingService;
